/**
 * ZandiHistoricalChart Component
 *
 * Recharts LineChart visualization showing 16-year EGI history for a submarket.
 * Replaces the legacy CSS-only bar chart with a proper time-series line chart.
 *
 * Design: Gold line on dark charcoal background, matching TrajectoryChart theme.
 */

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { EgiTimeSeriesPoint } from '../../types/rentGrowth';

export interface EgiHistoryPoint {
  year: string | number;
  egi: number;
}

interface ZandiHistoricalChartProps {
  /** API data from /rent-growth/history endpoint (preferred) */
  data?: EgiTimeSeriesPoint[];
  /** Legacy prop for backward compatibility */
  history?: EgiHistoryPoint[];
  /** Current EGI value (used with legacy prop only) */
  currentEgi?: number;
  /** 5-year CAGR (used with legacy prop only) */
  cagr5yr?: number;
  /** Submarket name for chart title */
  submarketName?: string;
}

interface YearlyDataPoint {
  year: string;
  egi: number;
}

/**
 * Aggregate monthly yyyymm data into yearly averages.
 */
function aggregateApiDataToYearly(points: EgiTimeSeriesPoint[]): YearlyDataPoint[] {
  const yearlyData = new Map<string, number[]>();

  points.forEach((point) => {
    const year = String(point.yyyymm).slice(0, 4);
    if (!yearlyData.has(year)) {
      yearlyData.set(year, []);
    }
    yearlyData.get(year)!.push(point.egi);
  });

  return Array.from(yearlyData.entries())
    .map(([year, egis]) => ({
      year,
      egi: Math.round(egis.reduce((a, b) => a + b, 0) / egis.length),
    }))
    .sort((a, b) => Number(a.year) - Number(b.year));
}

/**
 * Aggregate legacy history data into yearly averages.
 */
function aggregateLegacyDataToYearly(history: EgiHistoryPoint[]): YearlyDataPoint[] {
  const yearlyData = new Map<string, number[]>();

  history.forEach((point) => {
    const yearStr = String(point.year);
    const year = yearStr.length >= 6 ? yearStr.slice(0, 4) : yearStr;
    if (!yearlyData.has(year)) {
      yearlyData.set(year, []);
    }
    yearlyData.get(year)!.push(point.egi);
  });

  return Array.from(yearlyData.entries())
    .map(([year, egis]) => ({
      year,
      egi: Math.round(egis.reduce((a, b) => a + b, 0) / egis.length),
    }))
    .sort((a, b) => Number(a.year) - Number(b.year));
}

/**
 * Format EGI value as dollar amount per month.
 */
function formatEgi(value: number): string {
  return `$${value.toLocaleString()}/mo`;
}

export const ZandiHistoricalChart: React.FC<ZandiHistoricalChartProps> = ({
  data,
  history,
  submarketName = 'Submarket',
}) => {
  // Determine yearly data from API data or legacy history
  let yearlyData: YearlyDataPoint[];

  if (data && data.length > 0) {
    yearlyData = aggregateApiDataToYearly(data);
  } else if (history && history.length > 0) {
    yearlyData = aggregateLegacyDataToYearly(history);
  } else {
    return null;
  }

  if (yearlyData.length === 0) {
    return null;
  }

  // Calculate summary stats
  const firstEgi = yearlyData[0].egi;
  const lastEgi = yearlyData[yearlyData.length - 1].egi;
  const totalChange = lastEgi - firstEgi;
  const totalChangePct = firstEgi > 0 ? ((totalChange / firstEgi) * 100).toFixed(1) : '0.0';
  const yearSpan = yearlyData.length;
  const isPositive = totalChange >= 0;

  return (
    <div className="rounded-lg border border-slate-dark bg-charcoal p-6">
      {/* Title */}
      <h3 className="text-gold uppercase tracking-[0.12em] font-semibold text-sm mb-1">
        EGI History
      </h3>
      <p className="text-sm text-stone-700 mb-2">
        {submarketName} -- Effective Gross Income per unit (2BR benchmark)
      </p>

      {/* Summary line */}
      <p className="text-sm text-ivory/70 leading-relaxed mb-4">
        {yearSpan}-year change: ${firstEgi.toLocaleString()} to ${lastEgi.toLocaleString()} (
        <span className={isPositive ? 'text-emerald-400' : 'text-crimson'}>
          {isPositive ? '+' : ''}{totalChangePct}%
        </span>
        )
      </p>

      {/* Recharts LineChart */}
      <ResponsiveContainer width="100%" height={280}>
        <LineChart
          data={yearlyData}
          margin={{ top: 5, right: 20, left: 20, bottom: 5 }}
        >
          {/* Grid */}
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#d4d1cc"
            opacity={0.2}
          />

          {/* X Axis - years */}
          <XAxis
            dataKey="year"
            stroke="#d4d1cc"
            style={{
              fontSize: '11px',
              fontFamily: 'JetBrains Mono, monospace',
            }}
            interval="preserveStartEnd"
          />

          {/* Y Axis - dollar format */}
          <YAxis
            stroke="#d4d1cc"
            style={{
              fontSize: '11px',
              fontFamily: 'JetBrains Mono, monospace',
            }}
            tickFormatter={(value: number) => `$${value.toLocaleString()}`}
            domain={['auto', 'auto']}
          />

          {/* Tooltip */}
          <Tooltip
            contentStyle={{
              backgroundColor: '#f5f4f1',
              border: '1px solid #d4d1cc',
              borderRadius: '8px',
              fontFamily: 'JetBrains Mono, monospace',
            }}
            labelStyle={{ color: '#d4a853', fontWeight: 600 }}
            formatter={(value: number) => [formatEgi(value), 'EGI']}
          />

          {/* Gold EGI line */}
          <Line
            type="monotone"
            dataKey="egi"
            stroke="#d4a853"
            strokeWidth={3}
            name="EGI"
            dot={{ fill: '#d4a853', r: 3 }}
            activeDot={{ r: 6 }}
            connectNulls={true}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * Loading skeleton for the EGI chart.
 */
export const ZandiHistoricalChartSkeleton: React.FC = () => (
  <div className="rounded-lg border border-slate-dark bg-charcoal p-6 animate-pulse">
    <div className="h-4 w-32 bg-graphite rounded mb-2" />
    <div className="h-3 w-48 bg-graphite rounded mb-4" />
    <div className="h-[280px] bg-graphite/30 rounded" />
  </div>
);

export default ZandiHistoricalChart;
